import type { UseQueryResult } from '@tanstack/react-query';
import { useBasicQuery } from '@hooks/requests/utils/use_basic_query';
import { PARTNER_API_KEYS_PATH } from '@hooks/requests/utils/paths';

// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * An API key on the account, as returned by `GET /api/v1/partner/api-keys`
 * (sc-45102, "Show existing keys to the user (1 of 3)").
 *
 * Each key is a separate Auth0 M2M Application (`client_id`), read live
 * from Auth0's Management API rather than stored by the backend — per the
 * Sep 2 update to sc-45102. Auth0 has no timestamps on a Client, so
 * `createdAt` comes from the backend's own audit trail and is omitted for
 * keys that predate it. AC3's "most recent first" ordering is not met by
 * this design.
 *
 * No `id`/client_id here at all — the real client_id never leaves the
 * backend for this endpoint (the response used to include it, unmasked,
 * solely so revoke could target a key by it; that stopped). `maskedKey`
 * is the only form of it this type carries, and it doubles as what
 * revoke (sc-45103) sends back to `DELETE /api/v1/partner/api-keys/`:
 * the backend re-resolves the real client_id server-side by matching the
 * masked prefix against a fresh read of the org's keys. See
 * `useRevokeApiKeyMutation`.
 */
export interface PartnerApiKey {
  name: string | null;
  maskedKey: string;
  createdAt?: string;
  /**
   * Whether this is the key that most recently authenticated a request
   * (AC4). Computed by the system — never set by the partner.
   *
   * Optional because *how* this is computed is still being confirmed (see
   * sc-45102's Open Questions, unresolved as of the Sep 2 update) — the field
   * is typed as forward-compatible so `ApiKeysTable` can light up the moment
   * the backend starts sending it, without another round of type changes.
   */
  isActive?: boolean;
}

export interface UsePartnerApiKeysParams {
  /**
   * Defaults to `true`. Pass `false` for viewer roles: the endpoint is
   * admin-only (AC1) and would answer 403, so there is nothing to ask for.
   */
  enabled?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * The account's API keys (sc-45102). **Admin roles only** — both AceUp and
 * Partner viewer roles are refused by the API (AC1), not merely hidden here.
 *
 * Read-only: this story only lists existing keys. Creating (sc-45104),
 * revoking (sc-45103), and renaming (sc-45257) keys are separate stories and
 * should invalidate this query's cache via `PARTNER_API_KEYS_PATH` rather
 * than re-stating the path.
 */
export const usePartnerApiKeysQuery = ({
  enabled = true,
}: UsePartnerApiKeysParams = {}): UseQueryResult<PartnerApiKey[], Error> =>
  useBasicQuery<PartnerApiKey[]>(PARTNER_API_KEYS_PATH, undefined, { enabled });

export default usePartnerApiKeysQuery;
